import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiFolderOutline, mdiAlertOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import {
    useDocumentTypes,
    useUpdateDocument,
    refreshAssetDocumentFolders,
    refreshContractDocumentFolders,
    type AssetDocumentFolder,
} from '../../hooks/useQueries'
import { groupDocumentsByFolder } from '../../lib/document-folders'
import { getLocalizedName } from '../../lib/localization'
import { cn } from '../../lib/utils'

/**
 * "Move a document to another folder" (spec 2026-08-01-20).
 *
 * A folder of a layout is bound to document *types* (`document_type_uids` is
 * the folder's own type plus the descendants of that type no other folder
 * claims), and `groupDocumentsByFolder` files a document purely on its
 * `document_type_uid`. So there is no "folder membership" to write: **moving a
 * document is re-typing it**, and the dialog is built to say so out loud —
 * folder pick → type pick (only when ambiguous) → an AlertDialog naming the
 * `current → target` type change → the existing document PATCH.
 *
 * A folder that maps no type at all cannot receive a document (nothing would
 * change its grouping), so it is listed but disabled with a hint.
 */

export interface MovableDocument {
    uid: string
    name?: string
    document_type_uid?: string | null
}

interface DocumentMoveFolderDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    org: string
    /**
     * Asset whose resolved folder tree must be re-fetched after the move.
     * Mutually exclusive with `contractUid` — a folder tree belongs to exactly
     * one holder (spec 2026-08-13-01 §6).
     */
    assetUid?: string
    /** Contract whose resolved folder tree must be re-fetched after the move. */
    contractUid?: string
    /** Folders of the layout resolved for that holder. */
    folders: AssetDocumentFolder[]
    document: MovableDocument
}

export function DocumentMoveFolderDialog({
    open,
    onOpenChange,
    org,
    assetUid,
    contractUid,
    folders,
    document,
}: DocumentMoveFolderDialogProps) {
    const { t, i18n } = useTranslation('folders')
    const queryClient = useQueryClient()
    const { data: documentTypes = [] } = useDocumentTypes(org)
    const updateDocument = useUpdateDocument(org, document.uid)

    const [targetFolderUid, setTargetFolderUid] = useState<string | null>(null)
    const [targetTypeUid, setTargetTypeUid] = useState<string | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Re-arm on every (re)open — the dialog is mounted per document row.
    useEffect(() => {
        if (!open) return
        setTargetFolderUid(null)
        setTargetTypeUid(null)
        setConfirmOpen(false)
        setError(null)
    }, [open, document.uid])

    // Same traversal as the tree, so the dialog names and numbers a folder
    // exactly as the documents tab does. No documents are passed: only the
    // ordered/nested/numbered rows are needed here.
    const rows = useMemo(() => groupDocumentsByFolder(folders, []).rows, [folders])

    const typeName = useCallback(
        (uid?: string | null) => {
            if (!uid) return null
            const dt = documentTypes.find(d => d.uid === uid)
            if (!dt) return null
            if (typeof dt.name === 'object' && dt.name !== null) {
                return getLocalizedName(dt.name as Record<string, string>, i18n.language) || dt.code
            }
            return (dt.name as string | undefined) || dt.code
        },
        [documentTypes, i18n.language]
    )

    const currentFolderUid = useMemo(() => {
        if (!document.document_type_uid) return null
        return rows.find(r => (r.folder.document_type_uids ?? []).includes(document.document_type_uid as string))
            ?.folder.uid ?? null
    }, [rows, document.document_type_uid])

    const targetFolder = useMemo(
        () => rows.find(r => r.folder.uid === targetFolderUid)?.folder ?? null,
        [rows, targetFolderUid]
    )
    const targetTypeOptions = targetFolder?.document_type_uids ?? []

    const selectFolder = useCallback(
        (folder: AssetDocumentFolder) => {
            const types = folder.document_type_uids ?? []
            if (types.length === 0) return // not a valid target
            setError(null)
            setTargetFolderUid(folder.uid)
            // Exactly one mapped type ⇒ preselected, the type step disappears.
            setTargetTypeUid(types.length === 1 ? types[0] : null)
        },
        []
    )

    const currentTypeLabel = typeName(document.document_type_uid) ?? t('asset_view.move_dialog.no_type')
    const targetTypeLabel = typeName(targetTypeUid) ?? ''
    const targetFolderLabel = targetFolder
        ? getLocalizedName(targetFolder.name as Record<string, string>, i18n.language) || targetFolder.code
        : ''

    const applyMove = useCallback(async () => {
        if (!targetTypeUid) return
        setError(null)
        try {
            await updateDocument.mutateAsync({ document_type_uid: targetTypeUid })
            // Moving a document is re-typing it, so whichever holder's tree is
            // on screen has to be re-resolved.
            if (assetUid) refreshAssetDocumentFolders(queryClient, org, assetUid)
            if (contractUid) refreshContractDocumentFolders(queryClient, org, contractUid)
            setConfirmOpen(false)
            onOpenChange(false)
        } catch (err: unknown) {
            const payload = err as { fields?: Array<{ message?: string }>; title?: string; message?: string }
            setError(payload?.fields?.[0]?.message || payload?.title || payload?.message || t('asset_view.move_dialog.error'))
            setConfirmOpen(false)
        }
    }, [targetTypeUid, updateDocument, queryClient, org, assetUid, contractUid, onOpenChange, t])

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="document-move-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('asset_view.move_dialog.title')}</DialogTitle>
                        <DialogDescription>
                            {t('asset_view.move_dialog.description', { name: document.name || document.uid })}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <p className="text-xs text-muted-foreground" data-testid="document-move-current-type">
                            {t('asset_view.move_dialog.current_type', { type: currentTypeLabel })}
                        </p>

                        <div className="space-y-1" role="listbox" aria-label={t('asset_view.move_dialog.title')}>
                            {rows.map(({ folder, depth, number }) => {
                                const types = folder.document_type_uids ?? []
                                const disabled = types.length === 0
                                const isCurrent = folder.uid === currentFolderUid
                                const selected = folder.uid === targetFolderUid
                                return (
                                    <button
                                        key={folder.uid}
                                        type="button"
                                        role="option"
                                        aria-selected={selected}
                                        disabled={disabled || isCurrent}
                                        onClick={() => selectFolder(folder)}
                                        style={{ paddingLeft: 8 + depth * 16 }}
                                        className={cn(
                                            'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                                            selected ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/50',
                                            (disabled || isCurrent) && 'cursor-not-allowed opacity-50 hover:bg-transparent'
                                        )}
                                        data-testid={`document-move-folder-${folder.uid}`}
                                    >
                                        {number && (
                                            <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                                                {number}
                                            </span>
                                        )}
                                        <AppIcon tone="muted" path={mdiFolderOutline} size="sm" className="shrink-0" />
                                        <span className="truncate">
                                            {getLocalizedName(folder.name as Record<string, string>, i18n.language) || folder.code}
                                        </span>
                                        {disabled && (
                                            <span
                                                className="ml-auto shrink-0 text-xs text-muted-foreground"
                                                data-testid={`document-move-folder-no-type-${folder.uid}`}
                                            >
                                                {t('asset_view.move_dialog.folder_no_type')}
                                            </span>
                                        )}
                                        {!disabled && isCurrent && (
                                            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                                {t('asset_view.move_dialog.folder_current')}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>

                        {/* Type step: only when the target folder is ambiguous. */}
                        {targetFolder && targetTypeOptions.length > 1 && (
                            <div className="space-y-2">
                                <Label htmlFor="document-move-type-select">
                                    {t('asset_view.move_dialog.type_label')}
                                </Label>
                                <Select
                                    value={targetTypeUid ?? undefined}
                                    onValueChange={value => setTargetTypeUid(value)}
                                >
                                    <SelectTrigger id="document-move-type-select" data-testid="document-move-type-select">
                                        <SelectValue placeholder={t('asset_view.move_dialog.type_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72 [&>div]:!h-auto [&>div]:max-h-[17rem] [&>div]:overflow-y-auto">
                                        {targetTypeOptions.map(uid => (
                                            <SelectItem key={uid} value={uid} data-testid={`document-move-type-option-${uid}`}>
                                                {typeName(uid) || uid}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <p className="flex items-start gap-2 text-xs text-muted-foreground">
                            <AppIcon path={mdiAlertOutline} size="sm" className="mt-0.5 shrink-0" />
                            <span>{t('asset_view.move_dialog.type_change_hint')}</span>
                        </p>

                        {error && (
                            <p
                                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                                role="alert"
                                data-testid="document-move-error"
                            >
                                {error}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="document-move-cancel">
                            {t('asset_view.move_dialog.cancel')}
                        </Button>
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            disabled={!targetTypeUid || updateDocument.isPending}
                            data-testid="document-move-submit"
                        >
                            {t('asset_view.move_dialog.submit')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="document-move-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('asset_view.move_dialog.confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription data-testid="document-move-confirm-description">
                            {t('asset_view.move_dialog.confirm_description', {
                                name: document.name || document.uid,
                                folder: targetFolderLabel,
                                from: currentTypeLabel,
                                to: targetTypeLabel,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="document-move-confirm-cancel">
                            {t('asset_view.move_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={event => {
                                // Keep the AlertDialog mounted until the PATCH
                                // resolves, so a failure can be shown in the
                                // parent dialog instead of vanishing.
                                event.preventDefault()
                                void applyMove()
                            }}
                            data-testid="document-move-confirm-apply"
                        >
                            {t('asset_view.move_dialog.confirm_apply')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
